import { useState, useRef } from 'react';
import { Trash2 } from 'lucide-react';
import { useFetch } from '../../hooks/useFetch';
import { mediaApi } from '../../api/mediaApi';
import { Button } from '../../components/ui/Button';
import './MediaManager.css';

const API_URL = process.env.REACT_APP_API_URL;

export function MediaManager() {
  const [afficherNonUtilises, setAfficherNonUtilises] = useState(false);
  const { data: media, loading, refetch } = useFetch(
    () => mediaApi.list(undefined, afficherNonUtilises),
    [afficherNonUtilises]
  );
  const [altText, setAltText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [copieId, setCopieId] = useState(null);
  const fileInputRef = useRef(null);

  async function handleUpload(e) {
    e.preventDefault();
    const file = fileInputRef.current.files[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    try {
      await mediaApi.upload(file, altText);
      setAltText('');
      fileInputRef.current.value = '';
      refetch();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Supprimer ce média ? Les contenus qui l’utilisent perdront leur image.')) return;
    await mediaApi.remove(id);
    refetch();
  }

  function handleCopy(m) {
    navigator.clipboard?.writeText(`${API_URL}${m.chemin}`);
    setCopieId(m.id);
    setTimeout(() => setCopieId(null), 1500);
  }

  if (loading) return <p>Chargement...</p>;

  return (
    <section>
      <h1>Médiathèque</h1>
      <p className="media-manager__aide">
        Envoyez vos images ici, puis attachez-les à un contenu (Services, Actualités, Réalisations,
        Paramètres → image d'accueil) via le bouton <strong>« Choisir dans la médiathèque »</strong>{' '}
        présent dans chacun de ces formulaires.
      </p>

      <form className="admin-form" onSubmit={handleUpload}>
        <div className="admin-form__field">
          <label>Fichier (image, PDF ou vidéo — 10 Mo max)</label>
          <input type="file" ref={fileInputRef} required />
        </div>

        <div className="admin-form__field">
          <label>Texte alternatif (accessibilité / SEO)</label>
          <input value={altText} onChange={(e) => setAltText(e.target.value)} />
        </div>

        {error && <p className="admin-form__error">{error}</p>}

        <Button type="submit" disabled={uploading}>
          {uploading ? 'Envoi...' : 'Envoyer'}
        </Button>
      </form>

      <div className="admin-toolbar">
        <h2 className="media-manager__count">
          {media?.length || 0} média{media?.length === 1 ? '' : 's'}
          {afficherNonUtilises ? ' non utilisé(s)' : ''}
        </h2>
        <button
          type="button"
          className={`media-manager__unused-toggle ${afficherNonUtilises ? 'media-manager__unused-toggle--active' : ''}`}
          onClick={() => setAfficherNonUtilises((v) => !v)}
          aria-pressed={afficherNonUtilises}
        >
          <Trash2 size={14} aria-hidden="true" />
          Médias non utilisés
        </button>
      </div>

      {afficherNonUtilises && (
        <p className="media-manager__aide media-manager__aide--info">
          Ces images ne sont référencées dans aucun service, catégorie, article, page, témoignage ou
          paramètre du site — vous pouvez les supprimer en toute sécurité pour libérer de l'espace.
        </p>
      )}

      {!media?.length && (
        <p>
          {afficherNonUtilises
            ? 'Aucun média inutilisé — la médiathèque est propre !'
            : 'Aucun média envoyé pour le moment.'}
        </p>
      )}

      <div className="media-manager__grid">
        {media?.map((m) => (
          <figure className="media-manager__item" key={m.id}>
            {m.type === 'image' ? (
              <img src={`${API_URL}${m.chemin}`} alt={m.alt_text || m.nom_fichier} />
            ) : (
              <div className="media-manager__placeholder">{m.type.toUpperCase()}</div>
            )}
            <figcaption>
              <span className="media-manager__name">{m.nom_fichier}</span>
              <div className="media-manager__actions">
                <button type="button" onClick={() => handleCopy(m)}>
                  {copieId === m.id ? 'Copié !' : 'Copier le lien'}
                </button>
                <button type="button" className="media-manager__actions-delete" onClick={() => handleDelete(m.id)}>
                  Supprimer
                </button>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}
